// Global Instructions Rule Applied!

/**
 * InputManager TypeScript Type Definitions
 *
 * This file contains all type definitions for the InputManager component system,
 * providing strict typing for inline editing functionality in both table cells and forms.
 */

import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'

// ============================================================================
// Core Enums and Union Types
// ============================================================================

/**
 * Input mode for the InputManager
 * - 'read': Fields show values, click to edit (if editable)
 * - 'edit': Fields are always in edit mode
 */
export type InputMode = 'read' | 'edit'

/**
 * Union type for all possible field values
 */
export type FieldValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | { label: string; value: any; [key: string]: any }>
  | { [key: string]: any }

/** One entry of a list-valued field (multi-select, contacts, companies): a bare id or an option object. */
export type FieldValueListEntry = string | number | { label: string; value: any; [key: string]: any }

/**
 * `FieldValue` is the union of every field type's value because a form's single `onChange` serves all of its
 * fields. A handler that knows which field it is handling narrows with these guards rather than reading
 * properties off the union.
 */
export function isListFieldValue(value: FieldValue): value is FieldValueListEntry[] {
  return Array.isArray(value)
}

/**
 * A structured (non-list) value such as the valuation input's `{ preMoneyValue, postMoneyValue }`. The
 * `length?: never` member keeps arrays out of the narrowed type — an array satisfies a bare index signature.
 */
export type FieldValueRecord = { [key: string]: any } & { length?: never }

export function isRecordFieldValue(value: FieldValue): value is FieldValueRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** A plain scalar (text, number, or empty) — what the number and currency displays format. */
export function isScalarFieldValue(value: FieldValue): value is string | number | null | undefined {
  return value === null || value === undefined || typeof value === 'string' || typeof value === 'number'
}

/**
 * What a text input renders for a field value: a scalar as-is (empty for null/undefined), anything else as its
 * string form — the same string React would render for a non-scalar `value` prop, so nothing changes on screen.
 */
export function textFieldValue(value: FieldValue): string | number {
  return isScalarFieldValue(value) ? value || '' : String(value)
}

/** The list a list-valued field holds; anything else (an empty or scalar value) reads as no entries. */
export function listFieldValue(value: FieldValue): FieldValueListEntry[] {
  return isListFieldValue(value) ? value : []
}

/**
 * Label position for field labels
 * - 'above': Labels appear above the field
 * - 'inline': Labels appear to the left of the field (default)
 * - 'none': No labels are displayed
 */
export type LabelPosition = 'above' | 'inline' | 'none'

export type Alignment = 'left' | 'center' | 'right'

export enum FieldTypeEnum {
  TASKS = 'tasks',
  TEXT_ACTION = 'text-action',
  LINK = 'link',
  TEXT = 'text',
  NUMBER = 'number',
  CURRENCY = 'currency',
  PERCENT = 'percent',
  RATIO = 'ratio',
  SELECT = 'select',
  TEXTAREA = 'textarea',
  URL = 'url',
  EMAIL = 'email',
  PHONE = 'phone',
  COMPANY = 'company',
  COUNTRY = 'country',
  CITY = 'city',
  CONTACT = 'contact',
  VALUATION = 'valuation',
  DATE = 'date',
  TIME = 'time',
  RICHTEXT = 'richtext',
  SWITCH = 'switch',
  CHECKBOX = 'checkbox',
  DEAL = 'deal',
  PROSPECT = 'prospect',
  TAGS = 'tags'
}

/**
 * Supported field types for the InputManager
 */
export type FieldType =
  | FieldTypeEnum.LINK
  | FieldTypeEnum.TEXT_ACTION
  | FieldTypeEnum.TEXT
  | FieldTypeEnum.NUMBER
  | FieldTypeEnum.CURRENCY
  | FieldTypeEnum.PERCENT
  | FieldTypeEnum.SELECT
  | FieldTypeEnum.TEXTAREA
  | FieldTypeEnum.URL
  | FieldTypeEnum.EMAIL
  | FieldTypeEnum.PHONE
  | FieldTypeEnum.COMPANY
  | FieldTypeEnum.COUNTRY
  | FieldTypeEnum.CITY
  | FieldTypeEnum.CONTACT
  | FieldTypeEnum.VALUATION
  | FieldTypeEnum.DATE
  | FieldTypeEnum.TIME
  | FieldTypeEnum.RICHTEXT
  | FieldTypeEnum.SWITCH
  | FieldTypeEnum.CHECKBOX
  | FieldTypeEnum.DEAL
  | FieldTypeEnum.PROSPECT
  | FieldTypeEnum.TAGS

export enum SelectTypeEnum {
  LOOKUP = 'lookup',
  FILTER = 'filter'
}

/**
 * Size options for input components
 * - 'small': Compact size for dense layouts (e.g. Kanban cards)
 * - 'default': Standard size (maps to 'middle' for Ant Design components)
 * - 'middle' | 'large': Ant Design sizes
 */
export type InputSize = 'small' | 'middle' | 'large'

// ============================================================================
// Field Configuration Types
// ============================================================================

/**
 * Base configuration for all field types
 */
export interface BaseFieldConfig {
  /** Unique identifier for the field (supports "path__fieldname" format for patch requests) */
  key: string
  /** Display label for the field (used in form mode) */
  label?: string
  /** Type of field to render */
  type: FieldType
  /** Current value of the field */
  value: FieldValue
  /** Formatted display value (if different from raw value) */
  displayValue?: string | React.ReactNode
  /** Whether the field can be edited inline (default: true) */
  editable?: boolean
  /** Whether the field should auto-focus when rendered in edit mode */
  autoFocus?: boolean
  /** Whether the field is disabled (read-only but visible) */
  disabled?: boolean
  /** Whether the field is required for validation */
  required?: boolean
  /** Placeholder text for the input */
  placeholder?: string
  /** Size of the input component */
  size?: InputSize
  /** Custom CSS class name */
  className?: string
  /** Custom inline styles */
  style?: React.CSSProperties
  /** Group this field with the next field for side-by-side layout (e.g., Date/Time) */
  groupWithNext?: boolean
  /** Label position override for this specific field (overrides InputManager's labelPosition prop) */
  labelPosition?: LabelPosition
  /** Field mappings for multi-field PATCH requests */
  patchFields?: Array<{
    sourceKey: string // Key in the value object (e.g., 'preMoneyValue')
    targetField: string // Field name for API (e.g., 'pre_money_valuation')
  }>
  /** Override the default endpoint extraction for special cases */
  patchEndpoint?: string
  /** Field mappings for Redux state updates (API field name -> Redux field name) */
  reduxFieldMapping?: Record<string, string>
  /** Transform field value and return complete field set for multi-field PATCH */
  valueTransformer?: (value: any, currentRecord: any) => Record<string, any>
  /** Async gate run after validation, before save/patch. Return/resolve false to abort the change (edit state is reverted). */
  confirmPatch?: (newValue: any, currentRecord: Record<string, any>) => boolean | Promise<boolean>
  /** Explanation shown behind a help "?" beside the label. For a field whose
   *  VOCABULARY the user is not expected to know (Critical Technology Area),
   *  not for restating the label. Renders in every label layout. */
  labelTooltip?: React.ReactNode
}

/**
 * Configuration for text-based fields
 */
export interface TextFieldConfig extends BaseFieldConfig {
  type:
    | FieldTypeEnum.LINK
    | FieldTypeEnum.TEXT_ACTION
    | FieldTypeEnum.TEXT
    | FieldTypeEnum.URL
    | FieldTypeEnum.EMAIL
    | FieldTypeEnum.PHONE
  /** Maximum length for text input */
  maxLength?: number
  /** Minimum length for text input */
  minLength?: number
  /** Pattern for text validation (regex) */
  pattern?: string
  /** Whether to render value as hyperlink (default: false) */
  hyperlinked?: boolean
  /** Module name for opening form when hyperlink is clicked (e.g., 'companies', 'contacts', 'deals') */
  hyperlinkModule?: string
  /** Function to extract record ID from value/record for hyperlink navigation */
  getRecordId?: (value: any, record?: any) => string | null
  /** Custom hyperlink click handler (overrides default openFormInTab behavior) */
  onHyperlinkClick?: (module: string, recordId: string, title: string) => void
}

/**
 * Configuration for number-based fields
 */
export interface NumberFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.NUMBER | FieldTypeEnum.CURRENCY
  /** Minimum value allowed */
  min?: number
  /** Maximum value allowed */
  max?: number
  /** Step increment for number input */
  step?: number
  /** Currency symbol for currency fields */
  currencySymbol?: string
  /** Custom formatter function */
  /** antd InputNumber formatter: the display value may be undefined while the input is empty. */
  formatter?: (value: number | string | undefined, info?: { userTyping: boolean; input: string }) => string
  /** Custom parser function */
  /** Runs in antd's string mode: it must hand back the text the InputNumber will hold. */
  /** antd InputNumber parser: the display value may be undefined while the input is empty. */
  parser?: (value: string | undefined) => string
  /** Whether to show decimal places in display */
  showDecimal?: boolean
}

/**
 * Configuration for select fields
 */
export interface SelectFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.SELECT
  /** Options for the select dropdown */
  options?: SelectListEntry[]
  /** Whether to allow clearing the selection */
  allowClear?: boolean
  /** Whether to enable search functionality */
  showSearch?: boolean
  /** Custom filter function for search */
  filterOption?: (input: string, option: SelectListEntry) => boolean
  /** Type of select dropdown, determines where the options are coming from */
  selectType?: SelectTypeEnum
  /** Lookup category ID for lookup select dropdown */
  lookupCategory?: string
  /** Selection mode: 'single' for single select, 'multiple' for multi-select */
  mode?: 'single' | 'multiple'
  /** Whether to display values as tags/badges instead of text (default: false) */
  tagDisplay?: boolean
  /** Node appended below the option list (antd popupRender). Added for the
   *  fund-picker "show all funds" affordance: a gated picker must always offer
   *  a way to see what it is hiding, and an InputManager-rendered field cannot
   *  use core/components/FundSelect, which owns that footer itself. */
  optionsFooter?: React.ReactNode
  /** How the options are ordered in the dropdown: by label (the default, so
   *  a list of names reads alphabetically wherever it comes from) or as
   *  given, for a field whose options carry an order of their own - the
   *  quarters of a statement run, newest first. */
  optionOrder?: 'label' | 'given'
}

/**
 * Configuration for textarea fields
 */
export interface TextareaFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.TEXTAREA
  /** Number of rows for the textarea */
  rows?: number
  /** Maximum length for textarea input */
  maxLength?: number
  /** Whether to auto-resize the textarea */
  autoSize?: boolean | { minRows: number; maxRows: number }
}

/**
 * Configuration for company select fields
 */
export interface CompanyFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.COMPANY
  /** Mode for company selection */
  mode?: 'single' | 'multiple'
  /** Function to get company details by ID */
  getCompanyDetailsById?: (id: string) => any
  /** Callback when company details should update other fields */
  onCompanyDetailsUpdate?: (companyDetails: any) => void
  /** Whether to render company names as hyperlinks in read mode (default: true) */
  hyperlinked?: boolean
  /** Module name for opening form when hyperlink is clicked (default: 'companies') */
  hyperlinkModule?: string
  /** Function to extract company ID from value/record for hyperlink navigation */
  getRecordId?: (value: any, record?: any) => string | null
}

/**
 * Configuration for deal select fields
 */
export interface DealFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.DEAL
  /** Mode for deal selection */
  mode?: 'single' | 'multiple'
}

/**
 * Configuration for prospect select fields
 */
export interface ProspectFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.PROSPECT
  /** Mode for prospect selection */
  mode?: 'single' | 'multiple'
}

/**
 * Configuration for country select fields
 */
export interface CountryFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.COUNTRY
}

/**
 * Configuration for city select fields
 */
export interface CityFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.CITY
  /** Optional country filter for city search */
  country?: string
  /** Callback when country should be auto-populated from city selection */
  onCountryChange?: (countryCode: string) => void
}

/**
 * Configuration for contact select fields
 */
export interface ContactFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.CONTACT
  /** Mode for contact selection */
  mode?: 'single' | 'multiple'
  /** Optional company filter for contacts */
  companyId?: string | null
  /** Contact ID for single mode */
  contactId?: string
  /** Whether to show contact badges */
  badges?: boolean
  /** Options for the contact select dropdown */
  options?: SelectListEntry[]
  /** Whether to allow adding new contacts inline (default: true) */
  allowAddNew?: boolean
  /** Whether to show contact as tag/badge (default: true) - false shows as plain text */
  showValueTag?: boolean
  /** Whether to render contact badges as clickable hyperlinks (default: true) */
  hyperlinked?: boolean
  /** Module name for opening form when hyperlink is clicked (default: 'contacts') */
  hyperlinkModule?: string
  /** Function to extract contact ID from value/record for hyperlink navigation */
  getRecordId?: (value: any, record?: any) => string | null
}

/**
 * Configuration for valuation fields (pre/post money)
 */
export interface ValuationFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.VALUATION
  /** Currency symbol for display */
  currencySymbol?: string
  /** Pre-money valuation value */
  preMoneyValue?: number | null
  /** Post-money valuation value */
  postMoneyValue?: number | null
  /** Valuation type ('Pre' or 'Post') - used when value is a number */
  valuationType?: 'Pre' | 'Post'
  /** Minimum value allowed */
  min?: number
  /** Maximum value allowed */
  max?: number
  /** Step increment for number input */
  step?: number
  /** Whether to show the valuation type */
  showValuationType?: boolean
}

/**
 * Configuration for date fields
 */
export interface DateFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.DATE
  /** Format for date display */
  format?: string
  /** Whether to show time picker */
  showTime?: boolean
  /** Whether to allow clearing the date */
  allowClear?: boolean
}

/**
 * Configuration for time fields
 */
export interface TimeFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.TIME
  /** Format for time display */
  format?: string
  /** Whether to allow clearing the time */
  allowClear?: boolean
}

/**
 * Configuration for rich text editor fields
 */
export interface RichTextFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.RICHTEXT
  /** Placeholder text for the editor */
  placeholder?: string
  /** Custom configuration for Jodit editor */
  editorConfig?: any
  /**
   * Fires on every keystroke (Jodit 'change'), unlike onChange which is
   * blur-driven. Lets consumers read the latest content at save time even
   * when the editor hasn't blurred yet.
   */
  onLiveChange?: (value: string) => void
}

/**
 * Configuration for switch fields
 */
export interface SwitchFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.SWITCH
  /** Text to display when switch is checked */
  checkedChildren?: React.ReactNode
  /** Text to display when switch is unchecked */
  unCheckedChildren?: React.ReactNode
  /** Whether the switch is disabled */
  disabled?: boolean
  /** Size of the switch */
  size?: InputSize
}

/**
 * Configuration for checkbox fields
 */
export interface CheckboxFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.CHECKBOX
  /** Label text for the checkbox (used in edit mode) */
  checkboxLabel?: string
  /** Whether the checkbox is disabled */
  disabled?: boolean
}

export interface TagsFieldConfig extends BaseFieldConfig {
  type: FieldTypeEnum.TAGS
  /** Maximum number of tags allowed */
  maxLength?: number
  /** Whether the field is disabled */
  disabled?: boolean
}

/**
 * Union type for all field configurations
 */
export type FieldConfig =
  | TextFieldConfig
  | NumberFieldConfig
  | SelectFieldConfig
  | TextareaFieldConfig
  | CompanyFieldConfig
  | CountryFieldConfig
  | CityFieldConfig
  | ContactFieldConfig
  | ValuationFieldConfig
  | DateFieldConfig
  | TimeFieldConfig
  | RichTextFieldConfig
  | SwitchFieldConfig
  | CheckboxFieldConfig
  | DealFieldConfig
  | ProspectFieldConfig
  | TagsFieldConfig

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for the main InputManager component
 */
export interface InputManagerProps {
  /** Whether to show label colons */
  showLabelColons?: boolean
  /** Input mode for the component */
  inputMode: InputMode
  /** Array of field configurations */
  fields: FieldConfig[]
  /** Position of field labels */
  labelPosition?: LabelPosition
  alignment?: Alignment
  /** Callback for value changes */
  onChange?: (key: string, value: FieldValue) => void
  /** Callback after save (called even with auto-patch) */
  onSave?: (key: string, value: FieldValue) => void
  /** Enable automatic PATCH requests */
  enableAutoPatch?: boolean
  /** Patch ID for patch requests (record uuid) */
  patchId?: string
  /** Record ID for patch requests (record uuid) */
  recordId?: string
  /** Error handler */
  onError?: (key: string, error: Error) => void
  /** Callback after successful PATCH request */
  onPatchSuccess?: (recordId: string, updates: Record<string, any>) => void
  /** External validation errors */
  validationErrors?: Record<string, string>
  /**
   * Bump on every explicit submit (Save click). Forces the first field in
   * `validationErrors` to be scrolled into view and focused even when it is
   * the same field as last time or an input still holds focus — the typing
   * guard that protects inline auto-patch editing must not swallow an
   * explicit Save. Leave undefined for auto-patch surfaces.
   */
  validationErrorsVersion?: number
  /** Custom CSS class name */
  className?: string
  /** Custom inline styles */
  style?: React.CSSProperties
  /** Click handler for TEXT_ACTION and LINK fields */
  onClick?: (key: string, value: any, record?: any) => void
  /** Callback when field edit state changes */
  onFieldEdit?: (isEditing: boolean) => void
  /** Whether to wrap fields vertically in edit mode (label above, field below) */
  fieldWrapping?: boolean
  /** CSS class name to apply to labels and display values */
  textClassName?: string
  /** Override the calculated label width (e.g. 70 or '70px'). When set, all labels use this width. */
  maxLabelWidth?: number | string
  /** Full record data to pass to valueTransformer (overrides built currentRecord) */
  currentRecord?: Record<string, any>
}

/**
 * Props for individual InputManagerField component
 */
export interface InputManagerFieldProps {
  /** Field configuration */
  config: FieldConfig
  /** Whether this field is currently being edited */
  isEditing: boolean
  /** Whether this field is currently saving */
  isSaving: boolean
  /** Current temporary value while editing */
  tempValue: FieldValue
  /** Validation error message */
  validationError?: string
  /** Callback to start editing this field */
  onStartEdit: () => void
  /** Callback to confirm/save changes */
  onConfirm: (overrideValue?: any) => void
  /** Callback to cancel changes */
  onCancel: () => void
  /** Callback when value changes during editing */
  onValueChange: (value: FieldValue) => void
  /** Callback when field loses focus */
  onBlur: () => void
  /** Whether to hide the save/cancel action buttons */
  hideActions?: boolean
  /** Text alignment for field values */
  alignment?: Alignment
  /** Callback when field edit state changes */
  onFieldEdit?: (isEditing: boolean) => void
  /** Callback when edit mode changes (for form layout adjustments) */
  onEditModeChange?: (isEditing: boolean) => void
  /** Click handler for TEXT_ACTION and LINK fields */
  onClick?: (key: string, value: FieldValue, record?: any) => void
  /** Record data for onClick handler */
  record?: string | number | Record<string, any>
  /** CSS class name to apply to display values */
  textClassName?: string
  /** Input mode for the component */
  inputMode?: InputMode
  /** Label position for determining empty value display */
  labelPosition?: LabelPosition
}

// ============================================================================
// Hook Types
// ============================================================================

/**
 * Return type for useInputManager hook
 */
export interface UseInputManagerReturn {
  /** Currently editing field ID */
  editingFieldId: string | null
  /** Start editing a field */
  startEdit: (fieldId: string) => boolean
  /** Stop editing current field */
  stopEdit: (fieldId?: string) => void
  /** Check if a field is currently being edited */
  isFieldEditing: (fieldId: string) => boolean
  /** Check if any field is being edited */
  isAnyFieldEditing: boolean
}

/**
 * Return type for usePatchRequest hook
 */
export interface UsePatchRequestReturn {
  /** Execute a patch request */
  patchField: (
    fieldKey: string,
    value: any,
    patchId: string | number,
    patchFields?: Array<{ sourceKey: string; targetField: string }>,
    patchEndpoint?: string,
    valueTransformer?: (value: any, currentRecord: any) => Record<string, any>,
    currentRecord?: any
  ) => Promise<Record<string, any> | void>
  /** Whether a patch request is currently in progress */
  isPatching: boolean
  /** Last error from patch request */
  patchError: Error | null
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Configuration for automatic patch requests
 */
export interface PatchConfig {
  /** Base URL for API requests */
  baseUrl: string
  /** Authentication token */
  accessToken: string | null
  /** Request timeout in milliseconds */
  timeout?: number
}

/**
 * Validation error structure
 */
export interface ValidationError {
  /** Field key that has the error */
  fieldKey: string
  /** Error message */
  message: string
  /** Error type/code */
  type?: string
}

/**
 * Patch request result
 */
export interface PatchResult {
  /** Whether the request was successful */
  success: boolean
  /** Error message if request failed */
  error?: string
  /** Response data if successful */
  data?: any
}

// ============================================================================
// Event Handler Types
// ============================================================================

/**
 * Generic change event handler
 */
export type ChangeHandler = (value: FieldValue) => void

/**
 * Field-specific change event handler
 */
export type FieldChangeHandler = (key: string, value: FieldValue) => void

/**
 * Error event handler
 */
export type ErrorHandler = (key: string, error: Error) => void

/**
 * Save event handler
 */
export type SaveHandler = (key: string, value: FieldValue) => void | Promise<void>

// ============================================================================
// Display and Formatting Types
// ============================================================================

/**
 * Display value formatter function
 */
export type DisplayFormatter = (value: FieldValue) => string | React.ReactNode

/**
 * Input value parser function
 */
export type InputParser = (value: string) => FieldValue

/**
 * Validation function
 */
export type ValidationFunction = (value: FieldValue) => string | undefined

// ============================================================================
// Specialized Component Props
// ============================================================================

/**
 * Props for company select integration
 */
export interface CompanySelectProps {
  getCompanyDetailsById?: (id: string) => any
  onCompanyDetailsUpdate?: (companyDetails: any) => void
}

/**
 * Props for city select integration
 */
export interface CitySelectProps {
  country?: string
  onCountryChange?: (countryCode: string) => void
}

/**
 * Props for contact select integration
 */
export interface ContactSelectProps {
  mode?: 'single' | 'multiple'
  companyId?: string | null
  contactId?: string
  badges?: boolean
}
